import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Database, Cloud, Shield, FileText, CheckCircle2, TrendingUp, Clock, Zap, Server } from 'lucide-react';
import SEOHead from '../../components/SEOHead';

const technologies = [
  { name: 'Slack', icon: Server },
  { name: 'Smartsheet', icon: FileText },
  { name: 'Make.com', icon: Cloud },
  { name: 'Supabase', icon: Database },
  { name: 'Google Apps Script', icon: Shield },
];

const keyMetrics = [
  {
    icon: CheckCircle2,
    value: '100%',
    label: 'Duplicate Elimination',
    description: 'Zero duplicate client records across all onboarding workflows',
  },
  {
    icon: Zap,
    value: 'Zero',
    label: 'Manual Data Entry',
    description: 'Fully automated client onboarding from Slack to Smartsheet',
  },
  {
    icon: Clock,
    value: 'Real-Time',
    label: 'CSM Routing',
    description: 'Dynamic assignment replacing all manual allocation processes',
  },
  {
    icon: TrendingUp,
    value: '30+ Days',
    label: 'Invoice Alerts',
    description: 'Automated aging detection and escalation workflows',
  },
];

const highlights = [
  {
    category: 'Automation Excellence',
    items: [
      'Achieved 100% duplicate elimination across all client records with zero manual data entry at any workflow stage',
      'Automated complete CSM routing in real-time using dynamic mapping tables replacing all manual assignment',
      'Built timestamped audit trails across all client onboarding, change requests, and CSM interactions',
    ],
  },
  {
    category: 'Operational Efficiency',
    items: [
      'Eliminated 30+ day invoice monitoring blind spots with automated aging alerts and escalation workflows',
      'Replaced all disconnected manual processes with unified Slack-to-Smartsheet automation',
      'Delivered real-time operational visibility across the full client lifecycle from onboarding through payment',
    ],
  },
];

const challenges = [
  {
    title: 'Duplicate Records Proliferating Across Systems',
    icon: Database,
    problem:
      'Client submissions via Slack were being entered multiple times across Smartsheet without detection, causing data integrity failures, duplicate CSM assignments, and conflicting records that required manual cleanup.',
    solution:
      'Built idempotent duplicate detection logic at the Slack intake layer — every submission is validated against existing records before any downstream action, routing or record creation is triggered.',
    impact: '100% duplicate elimination with zero ongoing manual cleanup required',
  },
  {
    title: 'Manual CSM Assignment Creating Delays',
    icon: Server,
    problem:
      'Client-to-CSM assignment was done by managers reviewing incoming Slack messages manually — slow, inconsistent, and creating assignment delays and coverage gaps during high-volume periods.',
    solution:
      'Implemented a dynamic routing engine using configurable mapping tables and regex-based client classification, automatically assigning the correct CSM in real-time at the point of submission.',
    impact: 'Real-time automated CSM routing replacing all manual assignment across 350+ client accounts',
  },
  {
    title: 'No Invoice Aging Visibility',
    icon: FileText,
    problem:
      'Invoices older than 30 days had no monitoring mechanism — overdue accounts were only discovered reactively during manual reviews, by which point escalation was already overdue.',
    solution:
      'Built automated invoice aging monitoring with threshold-based alerting triggering Slack notifications and escalation workflows automatically at 30+ days, with no manual review required.',
    impact: 'Zero invoice aging blind spots with automated escalation from the moment threshold is crossed',
  },
  {
    title: 'No Audit Trail for Client Changes',
    icon: Shield,
    problem:
      'Change requests and onboarding events left no formal record — disagreements about what was agreed or when changes were made could not be resolved using any system data.',
    solution:
      'Built timestamped audit logging into every workflow stage, capturing all onboarding events, change requests, and CSM interactions in Supabase with real-time Smartsheet synchronisation.',
    impact: 'Complete audit trail across the full client lifecycle with full accountability at every stage',
  },
];

const MarketingAutomation = () => {
  return (
    <>
      <SEOHead
        title="Business Process Automation Platform Case Study | Agility AI"
        description="How Agility eliminated duplicate records, manual CSM assignments, and invoice blind spots for a 350+ client marketing agency through Slack-to-Smartsheet workflow automation."
        url="https://www.agilitytech.ai/case-studies/marketing-automation"
        type="article"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: 'Business Process Automation Platform',
          description:
            'Eliminating duplicate client records, manual CSM assignments, and invoice blind spots through intelligent Slack-to-Smartsheet workflow automation for a high-volume marketing agency managing 350+ client accounts.',
          author: { '@type': 'Organization', name: 'Agility AI' },
          publisher: { '@type': 'Organization', name: 'Agility AI' },
        }}
      />
      <div className="pt-20 bg-black">
        {/* Hero Section */}
        <section className="relative py-24 bg-gradient-to-r from-blue-900 to-black overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80"
              alt="Marketing Automation Banner"
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
          </div>
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-4xl">
              <div className="inline-flex items-center px-4 py-2 bg-teal-500/10 rounded-full border border-teal-500/20 mb-6">
                <span className="text-teal-400 text-sm font-medium">Marketing</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                Business Process Automation Platform
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed max-w-3xl">
                Eliminating duplicate client records, manual CSM assignments, and invoice blind spots through intelligent Slack-to-Smartsheet workflow automation for a high-volume marketing agency managing 350+ client accounts.
              </p>
            </div>
          </div>
        </section>

        {/* Key Metrics */}
        <section className="py-16 bg-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {keyMetrics.map((metric, index) => (
                <div
                  key={index}
                  className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300 text-center"
                >
                  <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <metric.icon className="h-6 w-6 text-teal-400" />
                  </div>
                  <div className="text-3xl font-bold text-white mb-2">{metric.value}</div>
                  <div className="text-teal-400 font-medium mb-2">{metric.label}</div>
                  <p className="text-gray-400 text-sm">{metric.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Project Details */}
        <section className="py-12 bg-gray-900">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Project Overview</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
                <h3 className="text-lg font-semibold text-teal-400 mb-2">Industry</h3>
                <p className="text-white">Marketing</p>
              </div>
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
                <h3 className="text-lg font-semibold text-teal-400 mb-2">Region</h3>
                <p className="text-white">USA</p>
              </div>
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
                <h3 className="text-lg font-semibold text-teal-400 mb-2">Project Size</h3>
                <p className="text-white">Multi-Workflow Enterprise Automation</p>
              </div>
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
                <h3 className="text-lg font-semibold text-teal-400 mb-2">Time Frame</h3>
                <p className="text-white">Q2 2025 - Completed</p>
              </div>
            </div>
          </div>
        </section>

        {/* Technologies */}
        <section className="py-12 bg-gray-900 border-t border-gray-800">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h3 className="text-xl font-bold text-white mb-6 text-center">Technology Stack</h3>
            <div className="flex flex-wrap justify-center gap-6">
              {technologies.map((tech, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-3 bg-gray-800/30 rounded-lg px-4 py-3 border border-gray-700/30"
                >
                  <tech.icon className="h-6 w-6 text-teal-400" />
                  <span className="text-gray-300 font-medium">{tech.name}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Challenge Overview */}
        <section className="py-20 bg-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-white mb-8">The Challenge</h2>
              <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 border border-gray-800/50">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-red-500/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                    <FileText className="h-6 w-6 text-red-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-4">
                      Manual Operations Across 350+ Client Accounts
                    </h3>
                    <p className="text-xl text-gray-300 leading-relaxed">
                      A fast-growing marketing agency managing 350+ active client accounts was running client onboarding, change requests, and invoice monitoring through entirely manual processes across disconnected tools. Duplicate client records proliferated across systems as Slack submissions were entered multiple times without detection. CSM assignments were made manually by managers reviewing incoming messages — slow, inconsistent, and creating delays during high-volume periods. Invoice aging past 30 days had no monitoring mechanism, discovered only through reactive manual reviews.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Results */}
        <section className="py-20 bg-black border-t border-gray-800">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-white mb-8 text-center">Transformational Results</h2>
              <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 border border-gray-800/50 mb-12">
                <div className="flex items-start space-x-4 mb-8">
                  <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle2 className="h-6 w-6 text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-4">
                      Unified Marketing Agency Automation
                    </h3>
                    <p className="text-xl text-gray-300 leading-relaxed">
                      We replaced a patchwork of disconnected manual processes with a unified, automated Slack-to-Smartsheet platform covering the full client lifecycle. Duplicate detection at intake eliminated all record proliferation. Dynamic CSM routing replaced manual assignment across 350+ accounts. Automated invoice aging alerts eliminated the 30+ day blind spots. And timestamped audit trails across every event gave leadership complete operational visibility — all with zero manual data entry.
                    </p>
                  </div>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-8">
                {highlights.map((category, index) => (
                  <div
                    key={index}
                    className="bg-gray-900/30 backdrop-blur-sm rounded-xl p-6 border border-gray-800/30"
                  >
                    <h4 className="text-lg font-bold text-teal-400 mb-4">{category.category}</h4>
                    <div className="space-y-4">
                      {category.items.map((item, idx) => (
                        <div key={idx} className="flex items-start space-x-3">
                          <CheckCircle2 className="flex-shrink-0 h-5 w-5 text-green-400 mt-1" />
                          <p className="text-gray-300 text-sm leading-relaxed">{item}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Challenges & Solutions */}
        <section className="py-20 bg-gray-900">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-12 text-center">Challenges & Solutions</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {challenges.map((challenge, index) => (
                <div
                  key={index}
                  className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-8 border border-gray-700/50 hover:bg-gray-800/70 transition-all duration-300"
                >
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center">
                      <challenge.icon className="h-6 w-6 text-teal-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white">{challenge.title}</h3>
                  </div>
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-lg font-semibold text-red-400 mb-3 flex items-center">
                        <span className="w-2 h-2 bg-red-400 rounded-full mr-3"></span>Problem
                      </h4>
                      <p className="text-gray-300 leading-relaxed pl-5">{challenge.problem}</p>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-green-400 mb-3 flex items-center">
                        <span className="w-2 h-2 bg-green-400 rounded-full mr-3"></span>Solution
                      </h4>
                      <p className="text-gray-300 leading-relaxed pl-5">{challenge.solution}</p>
                    </div>
                    <div className="bg-teal-500/10 rounded-lg p-4 border border-teal-500/20">
                      <h4 className="text-sm font-semibold text-teal-400 mb-2">Impact</h4>
                      <p className="text-teal-300 font-medium">{challenge.impact}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gradient-to-r from-blue-900 to-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="max-w-3xl mx-auto">
              <div className="w-16 h-16 bg-teal-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <FileText className="h-8 w-8 text-teal-400" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-6">
                Ready to Automate Your Agency Operations?
              </h2>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Contact our automation specialists to discover how intelligent workflow automation can eliminate manual processes across your client management operations.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center space-x-3 bg-teal-500 text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-teal-400 transition-all duration-300 group shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                <span>Get Started Today</span>
                <ArrowRight className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default MarketingAutomation;
